<template>
  <div class="page-container">
    <!-- CABECERA -->
    <div class="custom-card mb-4">
      <div class="d-flex align-items-center flex-wrap p-4">
        <!-- Avatar -->
        <div class="employee-avatar mr-4">
          {{ initials }}
        </div>

        <!-- Datos principales -->
        <div class="flex-grow-1">
          <h2 class="employee-full-name mb-1">{{ fullName }}</h2>
          <p class="text-muted mb-2">{{ identifierTypeLabel }}: {{ employee.identifier }}</p>
          <div class="d-flex flex-wrap employee-header-meta">
            <span class="mr-4">
              <i class="ri-building-line mr-1"></i>
              <small class="text-muted">Empresa:</small>
              <strong class="ml-1">{{ contractInfo('company') }}</strong>
            </span>
            <span class="mr-4">
              <i class="ri-team-line mr-1"></i>
              <small class="text-muted">Área:</small>
              <strong class="ml-1">{{ contractInfo('area') }}</strong>
            </span>
            <span class="mr-4">
              <i class="ri-briefcase-line mr-1"></i>
              <small class="text-muted">Cargo:</small>
              <strong class="ml-1">{{ contractInfo('position') }}</strong>
            </span>
            <span>
              <i class="ri-calendar-line mr-1"></i>
              <small class="text-muted">Ingreso:</small>
              <strong class="ml-1">{{ contractInfo('startDate') }}</strong>
            </span>
          </div>
        </div>

        <!-- Estado y botón -->
        <div class="d-flex flex-column align-items-end ml-4 mt-3 mt-md-0">
          <span :class="`badge mb-3 ${hasContract ? 'badge-green' : 'badge-gray'}`">
            {{ hasContract ? 'ACTIVO' : 'SIN CONTRATO' }}
          </span>
          <router-link
            :to="{ name: 'employee-edit', params: { id: String(employeeId) } }"
            class="button button-primary"
          >
            <i class="ri-edit-line mr-1"></i>Editar datos
          </router-link>
        </div>
      </div>

      <!-- Banner: documentos obligatorios pendientes — BLOQUEADO -->
      <div v-if="hasMissingDocuments" class="px-4 pb-3">
        <b-alert show variant="danger" class="mb-0 d-flex align-items-center">
          <i class="ri-lock-line mr-2" style="font-size: 1.25rem; flex-shrink:0;"></i>
          <div class="flex-grow-1">
            <strong>BLOQUEADO — Documentos obligatorios pendientes:</strong>
            {{ employee.missingDocuments.join(', ') }}.
            Las operaciones laborales están bloqueadas hasta regularizar.
          </div>
          <a href="#" @click.prevent="goToDocumentsTab" class="ml-3 small text-white">Ir a Documentos →</a>
        </b-alert>
      </div>
    </div>

    <!-- TABS -->
    <b-card no-body class="custom-card">
      <b-tabs card v-model="activeTab" class="employee-detail-tabs">

        <!-- Tab 1: Datos personales -->
        <b-tab>
          <template #title><i class="ri-user-line mr-1"></i>Datos personales</template>
          <div class="p-4">
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Tipo de documento</small>
                <strong>{{ identifierTypeLabel || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Número de documento</small>
                <strong>{{ employee.identifier || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Apellido paterno</small>
                <strong>{{ employee.lastNamePaternal || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Apellido materno</small>
                <strong>{{ employee.lastNameMaternal || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Nombres</small>
                <strong>{{ employee.name || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Fecha de nacimiento</small>
                <strong>{{ employee.birthDate || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Sexo</small>
                <strong>{{ genderLabel }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Estado civil</small>
                <strong>{{ employee.maritalStatus || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Nacionalidad</small>
                <strong>{{ employee.nationality || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Grupo sanguíneo</small>
                <strong>{{ employee.bloodType || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Discapacidad</small>
                <strong>{{ employee.hasDisability ? 'Sí' : 'No' }}</strong>
              </div>
              <template v-if="employee.hasDisability">
                <div class="col-md-4">
                  <small class="text-muted d-block">Tipo de discapacidad</small>
                  <strong>{{ employee.disabilityType || '---' }}</strong>
                </div>
                <div class="col-md-4">
                  <small class="text-muted d-block">Certificado CONADIS</small>
                  <strong>{{ employee.conadisCertificate || '---' }}</strong>
                </div>
              </template>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Nivel educativo</small>
                <strong>{{ employee.educationLevel || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Profesión</small>
                <strong>{{ employee.profession || '---' }}</strong>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab 2: Contacto -->
        <b-tab>
          <template #title><i class="ri-map-pin-line mr-1"></i>Contacto</template>
          <div class="p-4">
            <div class="row mb-3">
              <div class="col-md-6">
                <small class="text-muted d-block">Dirección</small>
                <strong>{{ employee.address || '---' }}</strong>
              </div>
              <div class="col-md-6">
                <small class="text-muted d-block">Ubigeo domicilio</small>
                <strong>{{ employee.ubigeoDescription || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <small class="text-muted d-block">Ubigeo nacimiento</small>
                <strong>{{ employee.birthUbigeoDescription || '---' }}</strong>
              </div>
              <div class="col-md-6">
                <small class="text-muted d-block">Teléfono personal</small>
                <strong>{{ employee.personalPhone || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <small class="text-muted d-block">Correo personal</small>
                <strong>{{ employee.personalEmail || '---' }}</strong>
              </div>
            </div>
            <hr />
            <h6 class="text-muted mb-3">Contacto de emergencia</h6>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Nombre</small>
                <strong>{{ employee.emergencyContact || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Parentesco</small>
                <strong>{{ employee.emergencyContactRelationship || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Teléfono</small>
                <strong>{{ employee.emergencyPhoneNumber || '---' }}</strong>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab 3: Banco y pensiones -->
        <b-tab>
          <template #title><i class="ri-bank-card-line mr-1"></i>Banco y pensiones</template>
          <div class="p-4">
            <h6 class="text-muted mb-3">Cuenta de haberes</h6>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Banco</small>
                <strong>{{ employee.bank ? employee.bank.name : '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Tipo de cuenta</small>
                <strong>{{ employee.accountType || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Número de cuenta</small>
                <strong>{{ employee.bankAccount || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">CCI</small>
                <strong>{{ employee.bankInterAccount || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Moneda</small>
                <strong>{{ employee.salaryCurrency || '---' }}</strong>
              </div>
            </div>
            <hr />
            <h6 class="text-muted mb-3">Cuenta CTS</h6>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Banco CTS</small>
                <strong>{{ employee.ctsBank ? employee.ctsBank.name : '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Número cuenta CTS</small>
                <strong>{{ employee.ctsAccount || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Moneda CTS</small>
                <strong>{{ employee.ctsCurrency || '---' }}</strong>
              </div>
            </div>
            <hr />
            <h6 class="text-muted mb-3">Sistema pensionario</h6>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Sistema</small>
                <strong>{{ employee.pensionSystem || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Entidad AFP</small>
                <strong>{{ employee.pensionEntity ? employee.pensionEntity.name : '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Código CUSPP</small>
                <strong>{{ employee.cusppCode || '---' }}</strong>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-4">
                <small class="text-muted d-block">Tipo comisión AFP</small>
                <strong>{{ employee.afpCommissionType || '---' }}</strong>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">5ta categoría</small>
                <strong>{{ employee.fifthCategoryStatus || '---' }}</strong>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Tab 4: Familia -->
        <b-tab>
          <template #title><i class="ri-parent-line mr-1"></i>Familia</template>
          <FamilyTab v-if="employeeId" :employee-id="employeeId" />
        </b-tab>

        <!-- Tab 5: Contratos vigentes -->
        <b-tab>
          <template #title>
            <i class="ri-file-list-line mr-1"></i>Contratos vigentes
            <span v-if="activeContracts.length > 1" class="badge badge-info ml-1">{{ activeContracts.length }}</span>
          </template>
          <ContractSummaryTab
            :employee-id="employeeId"
            :current-contracts="activeContracts"
          />
        </b-tab>

        <!-- Tab 5: Documentos -->
        <b-tab>
          <template #title><i class="ri-file-text-line mr-1"></i>Documentos</template>
          <DocumentsTab :employee-id="employeeId" />
        </b-tab>

        <!-- Tab 6: Vacaciones -->
        <b-tab>
          <template #title><i class="ri-calendar-check-line mr-1"></i>Vacaciones</template>
          <VacationsTab :employee-id="employeeId" />
        </b-tab>

        <!-- Tab 7: Descansos médicos -->
        <b-tab>
          <template #title><i class="ri-heart-pulse-line mr-1"></i>Descansos médicos</template>
          <MedicalLeavesTab :employee-id="employeeId" />
        </b-tab>

        <!-- Tab 8: Asistencia -->
        <b-tab>
          <template #title><i class="ri-time-line mr-1"></i>Asistencia</template>
          <AttendanceTab :employee-id="employeeId" />
        </b-tab>

        <!-- Tab 9: Desc. y adicionales -->
        <b-tab>
          <template #title><i class="ri-exchange-funds-line mr-1"></i>Desc. y adicionales</template>
          <PayrollMovementsTab :employee-id="employeeId" />
        </b-tab>


      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { Alert, Util } from 'wize-admin'
import { Constants as LocalConstants } from '@/core/utils'
import { EmployeeService } from '@/core/services'

import FamilyTab           from './tabs/FamilyTab.vue'
import ContractSummaryTab  from './tabs/ContractSummaryTab.vue'
import DocumentsTab        from './tabs/DocumentsTab.vue'
import VacationsTab        from './tabs/VacationsTab.vue'
import MedicalLeavesTab    from './tabs/MedicalLeavesTab.vue'
import AttendanceTab       from './tabs/AttendanceTab.vue'
import PayrollMovementsTab from './tabs/PayrollMovementsTab.vue'

export default {
  name: 'EmployeeDetails',
  components: {
    FamilyTab,
    ContractSummaryTab,
    DocumentsTab,
    VacationsTab,
    MedicalLeavesTab,
    AttendanceTab,
    PayrollMovementsTab,
  },

  data() {
    return {
      employee: {},
      activeTab: 0,
    }
  },

  computed: {
    employeeId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null
    },
    fullName() {
      return this.employee.fullName || '-'
    },
    initials() {
      const n = (this.employee.name || '').trim()
      const l = (this.employee.lastNamePaternal || '').trim()
      return ((n[0] || '') + (l[0] || '')).toUpperCase() || '?'
    },
    identifierTypeLabel() {
      const val = Util.searchValue(this.employee.identifierType, LocalConstants.IdentifierType)
      return val ? val.label : (this.employee.identifierType || '---')
    },
    genderLabel() {
      const g = this.employee.gender
      if (g === 1 || g === '1') return 'MASCULINO'
      if (g === 2 || g === '2') return 'FEMENINO'
      return g || '---'
    },
    activeContracts() {
      return Array.isArray(this.employee.currentContracts) ? this.employee.currentContracts : []
    },
    hasContract() {
      return this.activeContracts.length > 0
    },
    hasMissingDocuments() {
      return Array.isArray(this.employee.missingDocuments) && this.employee.missingDocuments.length > 0
    },
  },

  methods: {
    goToDocumentsTab() {
      this.activeTab = 5
    },
    contractInfo(field) {
      const list = this.activeContracts
      if (list.length === 0) return '---'
      const extract = (c) => {
        if (field === 'company')   return (c.company && c.company.name) || c.companyName || null
        if (field === 'area')      return (c.area && c.area.description) || c.areaName || null
        if (field === 'position')  return (c.position && c.position.description) || c.positionName || null
        if (field === 'startDate') return c.startDate || null
        return null
      }
      if (field === 'startDate') {
        const dates = list.map(extract).filter(Boolean).sort()
        return dates[0] || '---'
      }
      const values = list.map(extract).filter(Boolean)
      return values.length ? [...new Set(values)].join(', ') : '---'
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const response = await EmployeeService.get(id)
        this.employee = response.payload
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el colaborador')
        this.$router.push({ name: 'employee-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },

  created() {
    this.load(this.$route.params.id)
  },
}
</script>

<style lang="stylus" scoped>
.employee-avatar
  width 60px
  height 60px
  border-radius 50%
  background-color #4f46e5
  color white
  font-size 1.25rem
  font-weight bold
  display flex
  align-items center
  justify-content center
  flex-shrink 0

.employee-full-name
  font-size 1.25rem
  font-weight 700

.employee-header-meta
  font-size 0.9rem

.employee-detail-tabs
  ::v-deep .nav-tabs
    flex-wrap nowrap
    overflow-x auto
    .nav-link
      white-space nowrap
</style>
